(() => {
  const markers = new WeakMap();
  let closeTimer = 0;
  let submitting = false;
  let priceTimer = 0;
  let abortController = null;
  let errorObserver = null;
  let opened = false;
  let savedSummary = null;
  let cardRequest = 0;

  const drawer = () => document.querySelector('[data-hb-offer-drawer]');
  const sticky = () => document.querySelector('product-quick-add.hb-sticky-bar');
  const isOpen = () => opened;
  const fromCard = () => drawer()?.hasAttribute('data-hb-from-card');

  function pageOffer(selector) {
    return [...document.querySelectorAll(selector)].find((node) => !node.closest('[data-hb-card-root]'));
  }

  const hasOffers = () => {
    const root = drawer();
    if (root?.hasAttribute('data-hb-from-card')) {
      return Boolean(
        root.querySelector('[data-product-packs-configurator], [data-subscription-offer]')
        || document.querySelector('#hb-card-root [data-product-packs-configurator], #hb-card-root [data-subscription-offer]')
      );
    }
    return Boolean(pageOffer('[data-product-packs-configurator]') || pageOffer('[data-subscription-offer]'));
  };

  function park(node, slot) {
    if (!node || !slot) return;
    if (!markers.has(node)) {
      const marker = document.createComment('hb-offer-slot');
      if (node.parentNode) node.parentNode.insertBefore(marker, node);
      markers.set(node, marker);
    }
    if (node.parentElement !== slot) slot.appendChild(node);
  }

  function unpark(node) {
    if (!node) return;
    const marker = markers.get(node);
    if (marker?.isConnected) {
      marker.parentNode.insertBefore(node, marker);
      marker.remove();
    } else if (node.closest('[data-hb-offer-drawer]')) {
      node.remove();
    }
    markers.delete(node);
  }

  function parkAll() {
    const root = drawer();
    if (!root) return;
    const card = document.getElementById('hb-card-root');
    const packs = root.hasAttribute('data-hb-from-card')
      ? card?.querySelector('[data-product-packs-configurator]')
      : pageOffer('[data-product-packs-configurator]');
    const subscription = root.hasAttribute('data-hb-from-card')
      ? card?.querySelector('[data-subscription-offer]')
      : pageOffer('[data-subscription-offer]');
    park(packs, root.querySelector('[data-hb-slot="packs"]'));
    park(subscription, root.querySelector('[data-hb-slot="subscription"]'));
    const title = root.querySelector('.hb-offer-drawer__title');
    if (title) title.hidden = !root.querySelector('[data-hb-slot="packs"] [data-product-packs-configurator]');
  }

  function unparkAll() {
    const root = drawer();
    if (!root) return;
    unpark(root.querySelector('[data-hb-slot="packs"] [data-product-packs-configurator]'));
    unpark(root.querySelector('[data-hb-slot="subscription"] [data-subscription-offer]'));
  }

  function parsePrice(priceText) {
    if (!priceText) return 0;
    let value = String(priceText).replace(/[^\d,.\s]/g, '').replace(/\s/g, '');
    if (value.includes(',') && value.includes('.')) {
      value = value.lastIndexOf(',') > value.lastIndexOf('.')
        ? value.replace(/\./g, '').replace(',', '.')
        : value.replace(/,/g, '');
    } else if (value.includes(',')) {
      value = value.replace(',', '.');
    }
    const price = parseFloat(value);
    return Number.isFinite(price) && price > 0 ? price : 0;
  }

  function formatMoney(amount) {
    if (!amount) return '';
    const cents = Math.round(amount * 100);
    const moneyFormat = window.Shopify?.money_format || window.themeVariables?.settings?.moneyFormat;
    if (window.Shopify?.formatMoney && moneyFormat) {
      try {
        return window.Shopify.formatMoney(cents, moneyFormat)
          .replace(/(\d)\s+([^\d\s])/g, '$1$2')
          .replace(/([^\d\s])\s+(\d)/g, '$1$2');
      } catch (error) {
        // Fall through to Intl.
      }
    }
    const locale = window.Shopify?.locale || document.documentElement.lang || 'fr-FR';
    const currency = window.Shopify?.currency?.active || 'EUR';
    return new Intl.NumberFormat(locale, { style: 'currency', currency }).format(amount);
  }

  function subscriptionDiscount() {
    const offer = fromCard()
      ? drawer()?.querySelector('[data-subscription-offer]')
      : (pageOffer('[data-subscription-offer]') || document.querySelector('[data-subscription-offer]'));
    if (!offer) return 0;
    const standalone = offer.dataset.standalone === 'true';
    const checkbox = offer.querySelector('[data-subscription-checkbox]');
    if (!standalone && !checkbox?.checked) return 0;
    const selected = offer.querySelector('.fl-subscription-offer__select')?.selectedOptions?.[0];
    const source = selected?.getAttribute('data-subscription-plan-discount')
      ? selected
      : offer.querySelector('[data-subscription-plan-discount]');
    const discount = parseFloat(source?.getAttribute('data-subscription-plan-discount') || '0');
    return Number.isFinite(discount) ? discount : 0;
  }

  function plainMoney(value) {
    return String(value || '').replace(/<[^>]*>/g, '').replace(/\s+/g, ' ').trim();
  }

  function readFinalPrices() {
    const pack = fromCard()
      ? drawer()?.querySelector('[data-pack-item].active')
      : (pageOffer('[data-pack-item].active') || document.querySelector('[data-pack-item].active'));
    const fallback = document.querySelector('[data-hb-fallback-price]');
    let price = pack?.querySelector('.fl-product-pack__price .text-bold, .fl-pack-v2__final-price')?.textContent.trim() || '';
    let compare = pack?.querySelector('.fl-product-pack__price .line-through, .fl-pack-v2__compare-price')?.textContent.trim() || '';

    if (!price && fallback) {
      price = fallback.dataset.hbFallbackPrice || '';
      compare = fallback.dataset.hbFallbackCompare || '';
    }

    price = plainMoney(price);
    compare = plainMoney(compare);

    if (pack && !compare) {
      const quantity = parseInt(pack.dataset.packQuantity, 10) || 1;
      const unitCompare = parsePrice(pack.dataset.packComparePrice);
      const unitPrice = parsePrice(pack.dataset.productPrice);
      if (unitCompare > unitPrice) compare = formatMoney(unitCompare * quantity);
    }

    const discount = subscriptionDiscount();
    if (discount > 0 && price) {
      const base = parsePrice(price);
      const discounted = formatMoney(base * (1 - discount / 100));
      if (discounted) {
        if (!compare) compare = price;
        price = discounted;
      }
    }

    if (compare && compare === price) compare = '';
    return { price, compare };
  }

  function priceTargets() {
    if (fromCard()) return drawer()?.querySelectorAll('[data-hb-prices]') || [];
    return document.querySelectorAll('[data-hb-prices]');
  }

  function setLoading(loading) {
    priceTargets().forEach((element) => {
      element.classList.toggle('is-loading', loading);
    });
  }

  function commitPrices() {
    const next = readFinalPrices();
    priceTargets().forEach((element) => {
      const price = element.querySelector('[data-hb-offer-price]');
      const compare = element.querySelector('[data-hb-offer-compare]');
      if (price) price.textContent = next.price || '';
      if (compare) compare.textContent = next.compare || '';
      element.classList.remove('is-loading');
    });
  }

  function queuePriceRefresh() {
    setLoading(true);
    clearTimeout(priceTimer);
    priceTimer = setTimeout(commitPrices, 220);
  }

  function position() {
    const bar = sticky();
    const root = drawer();
    if (!root) return;
    const rect = bar?.getBoundingClientRect();
    if (!rect?.height) {
      root.style.setProperty('--hb-drawer-right', '20px');
      root.style.setProperty('--hb-drawer-bottom', '16px');
      root.style.setProperty('--hb-drawer-width', '480px');
      root.style.setProperty('--hb-sticky-offset', '0px');
      return;
    }
    const offset = Math.ceil(rect.height);
    root.style.setProperty('--hb-sticky-offset', `${offset}px`);
    root.style.setProperty('--hb-drawer-right', '20px');
    root.style.setProperty('--hb-drawer-bottom', `${offset + 12}px`);
    root.style.setProperty('--hb-drawer-width', '480px');
  }

  function setToggleState(open) {
    const bar = sticky();
    const label = open ? bar?.dataset.hbHideLabel : bar?.dataset.hbShowLabel;
    document.querySelectorAll('[data-hb-offer-toggle]').forEach((button) => {
      button.setAttribute('aria-expanded', open ? 'true' : 'false');
      const text = button.querySelector('[data-hb-offer-toggle-label]');
      if (text && label) text.textContent = label;
    });
  }

  function mainBuyButton(useCard) {
    if (useCard) return document.querySelector('#hb-card-root buy-buttons button[type="submit"]');
    return [...document.querySelectorAll('buy-buttons button[type="submit"]')].find((button) => {
      return !button.closest('[data-hb-card-root]') && !button.closest('[data-hb-offer-drawer]');
    });
  }

  function submitOffer(useCard) {
    if (submitting) return;
    const button = mainBuyButton(useCard);
    const triggers = useCard
      ? document.querySelectorAll('.hb-offer-drawer__cta button')
      : document.querySelectorAll('.hb-sb__cta button, .hb-offer-drawer__cta button');
    const setBusy = (busy) => triggers.forEach((element) => element.setAttribute('aria-busy', busy ? 'true' : 'false'));

    if (!button) {
      const formId = sticky()?.getAttribute('form');
      const form = formId ? document.getElementById(formId) : document.querySelector('form[is="product-form"]');
      form?.requestSubmit();
      return;
    }

    submitting = true;
    setBusy(true);
    button.click();

    const done = () => {
      submitting = false;
      setBusy(false);
    };

    if (button.getAttribute('aria-busy') === 'true') {
      const observer = new MutationObserver(() => {
        if (button.getAttribute('aria-busy') !== 'true') {
          observer.disconnect();
          done();
        }
      });
      observer.observe(button, { attributes: true, attributeFilter: ['aria-busy'] });
      setTimeout(() => {
        observer.disconnect();
        done();
      }, 8000);
      return;
    }

    done();
  }

  function mirrorError() {
    const source = document.querySelector('[data-pack-error-message]');
    const target = drawer()?.querySelector('[data-hb-offer-error]');
    if (!source || !target) return;
    const container = source.closest('.fl-packs__error-message');
    const text = source.textContent.trim();
    const hidden = !text || container?.classList.contains('fl-hidden');
    target.hidden = hidden;
    target.textContent = hidden ? '' : text;
  }

  function open() {
    const root = drawer();
    if (!root || opened || !hasOffers()) return;
    clearTimeout(closeTimer);
    opened = true;
    parkAll();
    position();
    mirrorError();
    root.hidden = false;
    document.documentElement.classList.add('hb-offer-drawer-open');
    setToggleState(true);
    requestAnimationFrame(() => {
      if (opened) root.classList.add('is-open');
    });
  }

  function close() {
    const root = drawer();
    if (!root || !opened) return;
    opened = false;
    root.classList.remove('is-open');
    setToggleState(false);
    document.documentElement.classList.remove('hb-offer-drawer-open');
    closeTimer = setTimeout(() => {
      const cardSession = root.hasAttribute('data-hb-from-card');
      unparkAll();
      if (cardSession) releaseCard(root);
      root.hidden = true;
    }, 280);
  }

  function releaseCard(root) {
    root?.removeAttribute('data-hb-from-card');
    document.getElementById('hb-card-root')?.remove();
    document.getElementById('hb-card-offer-styles')?.remove();
    const summary = root?.querySelector('.hb-offer-drawer__summary');
    if (summary && savedSummary != null) summary.innerHTML = savedSummary;
    savedSummary = null;
  }

  function settleDrawer() {
    clearTimeout(closeTimer);
    const root = drawer();
    if (!root) return;
    const cardSession = root.hasAttribute('data-hb-from-card');
    opened = false;
    root.classList.remove('is-open');
    setToggleState(false);
    document.documentElement.classList.remove('hb-offer-drawer-open');
    unparkAll();
    if (cardSession) releaseCard(root);
    root.hidden = true;
  }

  function onDocumentClick(event) {
    const offerSource = event.target.closest('[data-hb-card-offer]');
    const offerButton = offerSource ? event.target.closest('button') : null;
    if (offerSource && offerButton && offerSource.contains(offerButton)) {
      if (offerButton.dataset.hbAllow === 'true') return;
      if (offerButton.closest('form')?.querySelector('input[name="selling_plan"]')) return;
      event.preventDefault();
      event.stopPropagation();
      openFromCard(offerSource, offerButton);
      return;
    }

    if (event.target.closest('[data-hb-offer-close]')) {
      event.preventDefault();
      close();
      return;
    }

    if (event.target.closest('[data-hb-offer-toggle]')) {
      event.preventDefault();
      if (opened) close();
      else open();
      return;
    }

    const buyButton = event.target.closest('.hb-sb__cta button, .hb-offer-drawer__cta button');
    if (!buyButton || !hasOffers()) return;
    event.preventDefault();
    event.stopPropagation();
    if (!submitting) submitOffer(Boolean(buyButton.closest('.hb-offer-drawer__cta')) && fromCard());
  }

  function onKeydown(event) {
    if (!opened) return;
    if (event.key === 'Escape') {
      event.preventDefault();
      close();
      return;
    }
    if (event.key !== 'Tab') return;
    const focusable = drawer().querySelectorAll('button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])');
    const items = [...focusable].filter((element) => !element.disabled && element.offsetParent !== null);
    if (!items.length) return;
    const first = items[0];
    const last = items[items.length - 1];
    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  }

  function watchErrors() {
    errorObserver?.disconnect();
    const source = document.querySelector('[data-pack-error-message]');
    const container = source?.closest('.fl-packs__error-message');
    if (!container) return;
    errorObserver = new MutationObserver(() => {
      if (opened) mirrorError();
    });
    errorObserver.observe(container, { attributes: true, childList: true, subtree: true, characterData: true });
  }

  function watchSelection() {
    const configurator = document.querySelector('[data-product-packs-configurator]');
    if (!configurator) return;
    const observer = new MutationObserver((mutations) => {
      if (mutations.some((mutation) => mutation.target.matches?.('[data-pack-item]'))) queuePriceRefresh();
    });
    observer.observe(configurator, { subtree: true, attributes: true, attributeFilter: ['class'] });
  }

  function ensureDrawer(source) {
    const existing = drawer();
    if (existing) return existing;
    const root = document.createElement('div');
    root.className = 'hb-offer-drawer';
    root.setAttribute('data-hb-offer-drawer', '');
    root.hidden = true;
    root.innerHTML = `
      <div class="hb-offer-drawer__backdrop" data-hb-offer-close></div>
      <div class="hb-offer-drawer__panel" role="dialog" aria-modal="true">
        <div class="hb-offer-drawer__head">
          <p class="hb-offer-drawer__title"></p>
          <button type="button" class="hb-offer-drawer__close" data-hb-offer-close aria-label="Close">
            <svg width="14" height="14" viewBox="0 0 14 14" aria-hidden="true" focusable="false">
              <path d="M1 1l12 12M13 1L1 13" stroke="currentColor" stroke-width="1.6" stroke-linecap="round"></path>
            </svg>
          </button>
        </div>
        <div class="hb-offer-drawer__body">
          <div class="hb-offer-drawer__options">
            <div data-hb-slot="packs"></div>
            <div data-hb-slot="subscription"></div>
          </div>
          <div class="hb-offer-drawer__summary"></div>
          <button type="button" class="hb-offer-drawer__toggle" data-hb-offer-toggle aria-expanded="false">
            <span data-hb-offer-toggle-label></span>
          </button>
        </div>
        <div class="hb-offer-drawer__cta">
          <p class="hb-offer-drawer__error" data-hb-offer-error hidden></p>
          <button type="button">
            <span data-hb-add-label></span>
            <span class="hb-sb__prices is-loading" data-hb-prices>
              <span class="hb-sb__prices-sep">–</span>
              <span data-hb-offer-price></span>
              <span data-hb-offer-compare></span>
            </span>
          </button>
        </div>
      </div>
    `;
    const title = root.querySelector('.hb-offer-drawer__title');
    const addLabel = root.querySelector('[data-hb-add-label]');
    if (title) title.textContent = source?.dataset.hbChooseLabel || '';
    if (addLabel) addLabel.textContent = source?.dataset.hbAddLabel || '';
    document.body.appendChild(root);
    return root;
  }

  function isSameProduct(url) {
    try {
      const path = new URL(url, window.location.origin).pathname.replace(/\/$/, '');
      const here = window.location.pathname.replace(/\/$/, '');
      return (here === path || here.endsWith(path))
        && Boolean(drawer())
        && Boolean(pageOffer('[data-product-packs-configurator]') || pageOffer('[data-subscription-offer]'));
    } catch (error) {
      return false;
    }
  }

  function fillSummary(card) {
    const summary = drawer()?.querySelector('.hb-offer-drawer__summary');
    if (!summary || !card) return;
    if (savedSummary == null) savedSummary = summary.innerHTML;
    const image = card.querySelector('img');
    const title = card.querySelector('.product-card__title')?.textContent?.trim() || '';
    const reviews = card.querySelector('.fl-reviews-widget__text-title')?.textContent?.trim() || '';
    summary.replaceChildren();
    if (image) {
      const photo = document.createElement('img');
      photo.className = 'hb-offer-drawer__photo';
      photo.alt = '';
      photo.src = image.currentSrc || image.src;
      summary.appendChild(photo);
    }
    const product = document.createElement('div');
    product.className = 'hb-offer-drawer__product';
    if (reviews) {
      const score = document.createElement('p');
      score.className = 'hb-sb__score';
      score.textContent = reviews;
      product.appendChild(score);
    }
    const heading = document.createElement('p');
    heading.className = 'hb-offer-drawer__product-title';
    heading.textContent = title;
    product.appendChild(heading);
    summary.appendChild(product);
  }

  function mountStyles(doc) {
    const holder = document.createElement('div');
    holder.id = 'hb-card-offer-styles';
    doc.querySelectorAll('style').forEach((style) => {
      const text = style.textContent || '';
      if (!text.includes('.fl-subscription-offer') && !text.includes('.fl-product-pack')) return;
      const copy = document.createElement('style');
      copy.textContent = text;
      holder.appendChild(copy);
    });
    if (holder.childElementCount) document.body.appendChild(holder);
  }

  function prepareImportedRoot(holder) {
    holder.querySelectorAll('[id]').forEach((element) => {
      if (document.querySelectorAll(`[id="${CSS.escape(element.id)}"]`).length > 1) element.removeAttribute('id');
    });
    holder.querySelectorAll('[form]').forEach((element) => element.removeAttribute('form'));
    holder.querySelectorAll('.shopify-payment-button').forEach((element) => element.remove());
  }

  function loadPackScript(src) {
    if (window.hbInitPackSelector || !src) return Promise.resolve();
    const existing = [...document.scripts].find((script) => script.src && script.src.includes('fl-pack-selector'));
    if (existing) {
      return new Promise((resolve) => {
        if (window.hbInitPackSelector) resolve();
        else existing.addEventListener('load', () => resolve(), { once: true });
      });
    }
    return new Promise((resolve, reject) => {
      const script = document.createElement('script');
      script.src = src;
      script.onload = () => resolve();
      script.onerror = () => reject(new Error('pack script'));
      document.head.appendChild(script);
    });
  }

  async function openFromCard(source, button) {
    const url = source.dataset.hbCardOffer;
    if (!url || button?.getAttribute('aria-busy') === 'true') return;

    if (isSameProduct(url)) {
      if (fromCard()) settleDrawer();
      open();
      return;
    }

    const requestId = ++cardRequest;
    button?.setAttribute('aria-busy', 'true');
    try {
      const response = await fetch(url, { credentials: 'same-origin' });
      if (!response.ok) throw new Error('product fetch failed');
      const html = await response.text();
      if (requestId !== cardRequest) return;
      const doc = new DOMParser().parseFromString(html, 'text/html');
      const section = doc.querySelector('.shopify-section--main-product');
      const configurator = section?.querySelector('[data-product-packs-configurator]');
      const subscription = section?.querySelector('[data-subscription-offer]');
      if (!configurator && !subscription) {
        submitCardForm(button);
        return;
      }

      settleDrawer();
      const root = ensureDrawer(source);
      root.setAttribute('data-hb-from-card', '');
      mountStyles(doc);

      const holder = document.createElement('div');
      holder.id = 'hb-card-root';
      holder.setAttribute('data-hb-card-root', '');
      holder.className = 'product';
      const form = section.querySelector('form[is="product-form"]');
      if (form) holder.insertAdjacentHTML('beforeend', form.outerHTML);
      if (configurator) holder.insertAdjacentHTML('beforeend', configurator.outerHTML);
      if (subscription) holder.insertAdjacentHTML('beforeend', subscription.outerHTML);
      document.body.appendChild(holder);
      prepareImportedRoot(holder);

      if (subscription) {
        if (!window.hbInitSubscription) {
          const sourceScript = [...doc.querySelectorAll('script')].find((script) => script.textContent.includes('class SubscriptionToggle'));
          if (sourceScript) {
            const runtime = document.createElement('script');
            runtime.textContent = sourceScript.textContent;
            document.body.appendChild(runtime);
          }
        }
        window.hbInitSubscription?.();
      }

      if (configurator) {
        const packSrc = doc.querySelector('script[src*="fl-pack-selector"]')?.src;
        await loadPackScript(packSrc);
        window.hbInitPackSelector?.();
      }

      fillSummary(source.closest('.fl-card-product'));
      root.querySelectorAll('[data-hb-offer-toggle]').forEach((toggle) => {
        toggle.hidden = false;
      });
      open();
      queuePriceRefresh();
    } catch (error) {
      submitCardForm(button);
    } finally {
      button?.removeAttribute('aria-busy');
    }
  }

  function submitCardForm(button) {
    if (!button || button.dataset.hbAllow === 'true') return;
    button.dataset.hbAllow = 'true';
    if (button.form) button.form.requestSubmit(button);
    else button.click();
  }

  function init() {
    const root = drawer();
    const bar = sticky();
    if (!root || !bar) return;

    abortController?.abort();
    abortController = new AbortController();
    const { signal } = abortController;

    if (!pageOffer('[data-product-packs-configurator]') && !pageOffer('[data-subscription-offer]')) {
      document.querySelectorAll('[data-hb-offer-toggle]').forEach((button) => {
        if (!button.closest('[data-hb-offer-drawer]')) button.hidden = true;
      });
      if (!fromCard()) commitPrices();
      return;
    }

    const form = document.getElementById(bar.getAttribute('form'));
    form?.addEventListener('product:rerender', () => {
      requestAnimationFrame(() => {
        setToggleState(opened);
        queuePriceRefresh();
        if (opened) position();
      });
    }, { signal });

    watchErrors();
    watchSelection();
    if (!fromCard()) queuePriceRefresh();
  }

  function bindGlobal() {
    if (window.__hbOfferDrawerBound) return;
    window.__hbOfferDrawerBound = true;
    document.addEventListener('click', onDocumentClick, true);
    document.addEventListener('click', (event) => {
      if (event.target.closest('[data-pack-item]')) queuePriceRefresh();
    });
    document.addEventListener('change', (event) => {
      if (event.target.closest('[data-subscription-offer]')) queuePriceRefresh();
    });
    document.addEventListener('keydown', onKeydown);
    document.addEventListener('cart:refresh', () => {
      if (opened) close();
    });
    window.addEventListener('resize', () => {
      if (opened) position();
    });
  }

  bindGlobal();

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  if (!window.__hbOfferDrawerSectionListener) {
    window.__hbOfferDrawerSectionListener = true;
    document.addEventListener('shopify:section:load', (event) => {
      if (event.target.querySelector?.('[data-hb-offer-drawer]')) init();
    });
  }
})();
